"use client";

import { useMemo, useState } from "react";

export default function GenerateXml() {
    const [adId, setAdId] = useState("lshape-ad-001");
    const [adSystem, setAdSystem] = useState("L-Shape CTV System");
    const [adTitle, setAdTitle] = useState("L-Shape Interactive Ad");
    const [description, setDescription] = useState("L-Shape Video Ad");
    const [errorUrl, setErrorUrl] = useState(
        "https://example.com/error?code=[ERRORCODE]"
    );
    const [impressionUrl, setImpressionUrl] = useState(
        "https://example.com/impression"
    );
    const [duration, setDuration] = useState("00:00:30");

    const [trackStart, setTrackStart] = useState(
        "https://example.com/tracking/start"
    );
    const [trackFirstQuartile, setTrackFirstQuartile] = useState(
        "https://example.com/tracking/firstQuartile"
    );
    const [trackMidpoint, setTrackMidpoint] = useState(
        "https://example.com/tracking/midpoint"
    );
    const [trackThirdQuartile, setTrackThirdQuartile] = useState(
        "https://example.com/tracking/thirdQuartile"
    );
    const [trackComplete, setTrackComplete] = useState(
        "https://example.com/tracking/complete"
    );
    const [trackMute, setTrackMute] = useState(
        "https://example.com/tracking/mute"
    );
    const [trackUnmute, setTrackUnmute] = useState(
        "https://example.com/tracking/unmute"
    );
    const [trackPause, setTrackPause] = useState(
        "https://example.com/tracking/pause"
    );
    const [trackResume, setTrackResume] = useState(
        "https://example.com/tracking/resume"
    );
    const [clickThrough, setClickThrough] = useState(
        "https://example.com/landing"
    );
    const [clickTracking, setClickTracking] = useState(
        "https://example.com/tracking/click"
    );

    const [mediaMp4, setMediaMp4] = useState(
        "https://test-pages.b-cdn.net/MTN_16-9.mp4"
    );

    // Collapsed states for cards
    const [isAdMetaOpen, setIsAdMetaOpen] = useState(true);
    const [isTrackingOpen, setIsTrackingOpen] = useState(true);
    const [isClicksMediaOpen, setIsClicksMediaOpen] = useState(true);

    const xml = useMemo(() => {
        const safeCdata = (val) => (val ? `<![CDATA[${val}]]>` : "");
        const tagIfValue = (tag, val, extra = "") =>
            val ? `\t\t\t<${tag}${extra}>${safeCdata(val)}</${tag}>\n` : "";
        const trackingTag = (event, val) =>
            val
                ? `\t\t\t\t\t\t\t<Tracking event="${event}">${safeCdata(val)}</Tracking>\n`
                : "";

        return (
            `<VAST\n` +
            `\txmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:noNamespaceSchemaLocation="vast.xsd" version="3.0">\n` +
            `\t<Ad id="${adId}">\n` +
            `\t\t<InLine>\n` +
            tagIfValue("AdSystem", adSystem) +
            tagIfValue("AdTitle", adTitle) +
            tagIfValue("Description", description) +
            tagIfValue("Error", errorUrl) +
            tagIfValue("Impression", impressionUrl) +
            `\t\t\t<Creatives>\n` +
            `\t\t\t\t<Creative sequence="1">\n` +
            `\t\t\t\t\t<Linear>\n` +
            tagIfValue("Duration", duration) +
            `\t\t\t\t\t\t<TrackingEvents>\n` +
            trackingTag("start", trackStart) +
            trackingTag("firstQuartile", trackFirstQuartile) +
            trackingTag("midpoint", trackMidpoint) +
            trackingTag("thirdQuartile", trackThirdQuartile) +
            trackingTag("complete", trackComplete) +
            trackingTag("mute", trackMute) +
            trackingTag("unmute", trackUnmute) +
            trackingTag("pause", trackPause) +
            trackingTag("resume", trackResume) +
            `\t\t\t\t\t\t</TrackingEvents>\n` +
            `\t\t\t\t\t\t<VideoClicks>\n` +
            tagIfValue("ClickThrough", clickThrough, ' id="clickthrough"') +
            tagIfValue("ClickTracking", clickTracking, ' id="clicktracking"') +
            `\t\t\t\t\t\t</VideoClicks>\n` +
            `\t\t\t\t\t\t<MediaFiles>\n` +
            (mediaMp4
                ? `\t\t\t\t\t\t\t<MediaFile delivery="progressive" type="video/mp4">${safeCdata(mediaMp4)}</MediaFile>\n`
                : "") +
            `\t\t\t\t\t\t</MediaFiles>\n` +
            `\t\t\t\t\t</Linear>\n` +
            `\t\t\t\t</Creative>\n` +
            `\t\t\t</Creatives>\n` +
            `\t\t</InLine>\n` +
            `\t</Ad>\n` +
            `</VAST>`
        );
    }, [
        adId,
        adSystem,
        adTitle,
        description,
        errorUrl,
        impressionUrl,
        duration,
        trackStart,
        trackFirstQuartile,
        trackMidpoint,
        trackThirdQuartile,
        trackComplete,
        trackMute,
        trackUnmute,
        trackPause,
        trackResume,
        clickThrough,
        clickTracking,
        mediaMp4
    ]);

    // const download = () => {
    //     const blob = new Blob([xml], { type: "application/xml;charset=utf-8" });
    //     const url = URL.createObjectURL(blob);
    //     const a = document.createElement("a");
    //     a.href = url;
    //     a.download = `${adId || "vast"}.xml`;
    //     document.body.appendChild(a);
    //     a.click();
    //     a.remove();
    //     URL.revokeObjectURL(url);
    // };

    // const copyToClipboard = () => {
    //     navigator.clipboard.writeText(xml);
    //     alert("XML copied to clipboard!");
    // };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30 p-2">
            {/* Header */}
            <div className="mb-8">
                {/* Status Bar */}
                <div className="bg-white rounded-2xl shadow-sm border border-green-200 overflow-hidden mb-8">
                    <div className="p-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-3 text-green-600">
                                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                                    <span className="font-medium">VAST 3.0 XML is valid and ready for use</span>
                                </div>
                                <div className="h-6 w-px bg-gray-300"></div>
                                <div className="text-sm text-gray-600">
                                    <span className="font-medium">{xml.split('\n').length}</span> lines •
                                    <span className="font-medium ml-1">11</span> tracking events
                                </div>
                            </div>
                            {/* <div className="flex items-center gap-3">
                                <button
                                    onClick={copyToClipboard}
                                    className="px-4 cursor-pointer py-2 bg-gradient-to-r from-slate-800 to-slate-700 text-white rounded-lg font-medium hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-2"
                                >
                                    Copy XML
                                </button>
                                <button
                                    onClick={download}
                                    className="px-4 cursor-pointer py-2 bg-gradient-to-r from-slate-800 to-slate-700 text-white rounded-lg font-medium hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-2"
                                >
                                    Download XML
                                </button>
                            </div> */}
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid gap-8 lg:grid-cols-2">
                {/* Form Section */}
                <div className="space-y-6">
                    {/* Ad Meta Card - Collapsible */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                        <div
                            className="bg-gradient-to-r from-slate-800 to-slate-700 p-4 cursor-pointer"
                            onClick={() => setIsAdMetaOpen(!isAdMetaOpen)}
                        >
                            <div className="flex items-center justify-between">
                                <h2 className="text-md font-semibold text-white flex items-center gap-2">
                                    <svg className={`w-5 h-5 transform transition-transform ${isAdMetaOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                    Ad Metadata
                                </h2>
                                <div className="flex items-center gap-2">
                                    <span className="text-white text-sm">Required</span>
                                    <svg className={`w-5 h-5 text-white transform transition-transform ${isAdMetaOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                        {isAdMetaOpen && (
                            <div className="p-6 space-y-4">
                                {[
                                    { label: "Ad ID", value: adId, onChange: setAdId, placeholder: "Enter unique ad identifier" },
                                    { label: "Ad System", value: adSystem, onChange: setAdSystem, placeholder: "Enter ad system name" },
                                    { label: "Ad Title", value: adTitle, onChange: setAdTitle, placeholder: "Enter ad title" },
                                    { label: "Description", value: description, onChange: setDescription, placeholder: "Enter ad description" },
                                    { label: "Duration (HH:MM:SS)", value: duration, onChange: setDuration, placeholder: "00:00:30" },
                                ].map((field, index) => (
                                    <div key={index}>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {field.label}
                                        </label>
                                        <input
                                            type="text"
                                            value={field.value}
                                            onChange={(e) => field.onChange(e.target.value)}
                                            placeholder={field.placeholder}
                                            className="w-full px-4 py-3 text-black border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50/50 hover:bg-white placeholder-gray-400"
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Tracking Events Card - Collapsible */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                        <div
                            className="bg-gradient-to-r from-slate-800 to-slate-700 p-4 cursor-pointer"
                            onClick={() => setIsTrackingOpen(!isTrackingOpen)}
                        >
                            <div className="flex items-center justify-between">
                                <h2 className="text-md font-semibold text-white flex items-center gap-2">
                                    <svg className={`w-5 h-5 transform transition-transform ${isTrackingOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                    Tracking Events (Pixel URLs)
                                </h2>
                                <div className="flex items-center gap-2">
                                    <svg className={`w-5 h-5 text-white transform transition-transform ${isTrackingOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                        {isTrackingOpen && (
                            <div className="p-6 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {[
                                        { label: "Error URL", value: errorUrl, onChange: setErrorUrl, placeholder: "Enter error tracking URL" },
                                        { label: "Impression URL", value: impressionUrl, onChange: setImpressionUrl, placeholder: "Enter impression tracking URL" },
                                        { label: "Start", value: trackStart, onChange: setTrackStart, placeholder: "Enter start tracking URL" },
                                        { label: "First Quartile", value: trackFirstQuartile, onChange: setTrackFirstQuartile, placeholder: "Enter first quartile tracking URL" },
                                        { label: "Midpoint", value: trackMidpoint, onChange: setTrackMidpoint, placeholder: "Enter midpoint tracking URL" },
                                        { label: "Third Quartile", value: trackThirdQuartile, onChange: setTrackThirdQuartile, placeholder: "Enter third quartile tracking URL" },
                                        { label: "Mute", value: trackMute, onChange: setTrackMute, placeholder: "Enter mute tracking URL" },
                                        { label: "Unmute", value: trackUnmute, onChange: setTrackUnmute, placeholder: "Enter unmute tracking URL" },
                                        { label: "Pause", value: trackPause, onChange: setTrackPause, placeholder: "Enter pause tracking URL" },
                                        { label: "Resume", value: trackResume, onChange: setTrackResume, placeholder: "Enter resume tracking URL" },
                                        { label: "Complete", value: trackComplete, onChange: setTrackComplete, placeholder: "Enter complete tracking URL" },
                                    ].map((field, index) => (
                                        <div key={index}>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {field.label}
                                            </label>
                                            <input
                                                type="text"
                                                value={field.value}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                placeholder={field.placeholder}
                                                className="w-full px-3 py-2 border text-black border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-sm bg-gray-50/50 hover:bg-white placeholder-gray-400"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Clicks & Media Card - Collapsible */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                        <div
                            className="bg-gradient-to-r from-slate-800 to-slate-700 p-4 cursor-pointer"
                            onClick={() => setIsClicksMediaOpen(!isClicksMediaOpen)}
                        >
                            <div className="flex items-center justify-between">
                                <h2 className="text-md font-semibold text-white flex items-center gap-2">
                                    <svg className={`w-5 h-5 transform transition-transform ${isClicksMediaOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                    Clicks & Media
                                </h2>
                                <div className="flex items-center gap-2">
                                    <span className="text-white text-sm">Media URLs</span>
                                    <svg className={`w-5 h-5 text-white transform transition-transform ${isClicksMediaOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                        {isClicksMediaOpen && (
                            <div className="p-6 space-y-4">
                                {[
                                    { label: "ClickThrough URL", value: clickThrough, onChange: setClickThrough, placeholder: "Enter click through URL" },
                                    { label: "ClickTracking URL", value: clickTracking, onChange: setClickTracking, placeholder: "Enter click tracking URL" },
                                    { label: "MediaFile MP4 URL", value: mediaMp4, onChange: setMediaMp4, placeholder: "Enter media file URL" },
                                ].map((field, index) => (
                                    <div key={index}>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {field.label}
                                        </label>
                                        <input
                                            type="text"
                                            value={field.value}
                                            onChange={(e) => field.onChange(e.target.value)}
                                            placeholder={field.placeholder}
                                            className="w-full px-4 py-3 border text-black border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 bg-gray-50/50 hover:bg-white placeholder-gray-400"
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Preview Section */}
                <div className="space-y-6">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
                        <div className="bg-gradient-to-r from-slate-800 to-slate-700 p-4">
                            <div className="flex items-center justify-between">
                                <h2 className="text-md font-semibold text-white flex items-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                    </svg>
                                    XML Preview
                                </h2>
                            </div>
                        </div>
                        <div className="flex-1 p-6">
                            <div className="bg-gray-900 rounded-xl p-4 h-full overflow-auto">
                                <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap leading-relaxed">
                                    {xml}
                                </pre>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
}
